import { useState } from "react";

export default function VariantTable({
  variants,
  visibleIndices,
  selected,
  masterCheckboxRef,
  allVisibleSelected,
  onSelectAll,
  onToggle,
  onUpdate,
  onAutoFillRowSku,
  onCopySku,
  onToggleActive,
  options,
  issues,
  highlightedIssueRows,
  conflictingSkus,
  inputBaseClass,
  integrated = false,
}) {
  const [togglingRows, setTogglingRows] = useState(new Set());

  const handleToggle = async (idx) => {
    if (togglingRows.has(idx)) return;
    setTogglingRows((prev) => new Set(prev).add(idx));
    try {
      await onToggleActive(idx);
    } finally {
      setTogglingRows((prev) => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }
  };
  return (
    <div
      className={
        integrated
          ? "overflow-hidden bg-white dark:bg-[#121214]"
          : "overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-[#121214]"
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs text-zinc-600 dark:bg-[#0f0f10] dark:text-zinc-400">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <input
                  ref={masterCheckboxRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={onSelectAll}
                />
              </th>
              <th className="px-3 py-2.5 text-left">Variant</th>
              <th className="px-3 py-2.5 text-left">SKU</th>
              <th className="px-3 py-2.5 text-left">Price</th>
              <th className="px-3 py-2.5 text-left">Checks</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {visibleIndices.map((idx) => {
              const v = variants[idx];
              const row = issues[idx];
              const sku = (v.sku || "").trim();
              const skuBad = row.errs.skuMissing || row.errs.skuDup;
              const skuConflict = Boolean(sku) && conflictingSkus?.has(sku.toUpperCase());
              const priceBad = row.errs.priceInvalid;
              const rowHighlighted = highlightedIssueRows.has(idx);

              const orderedOptionValues =
                options.length > 0
                  ? options
                      .filter((o) => (o.name || "").trim())
                      .map((o) => (v.optionValues || {})[(o.name || "").trim()])
                      .filter((val) => val != null)
                  : Object.values(v.optionValues || {});

              return (
                <tr
                  key={idx}
                  data-sku-conflict={skuConflict ? "true" : undefined}
                  className={
                    "group align-middle transition-colors hover:bg-zinc-50/80 dark:hover:bg-zinc-900/25 " +
                    (v.isActive === false ? "bg-zinc-50/70 dark:bg-zinc-900/20 " : "bg-white dark:bg-[#121214] ") +
                    (rowHighlighted || skuConflict
                      ? "ring-1 ring-inset ring-amber-400/80 dark:ring-amber-700/70"
                      : "")
                  }
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(idx)}
                      onChange={() => onToggle(idx)}
                    />
                  </td>

                  <td className="px-3 py-2.5">
                    <div className="flex min-w-[180px] flex-wrap items-center gap-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {orderedOptionValues.map((val, optionIdx) => (
                          <span
                            key={`${idx}-${optionIdx}-${val}`}
                            className="inline-flex items-center rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-700
                                       dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                          >
                            {val}
                          </span>
                        ))}
                      </div>

                      <div className="ml-auto flex items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={v.isActive !== false}
                          aria-label={v.isActive === false ? "Activate variant" : "Deactivate variant"}
                          disabled={togglingRows.has(idx)}
                          onClick={() => handleToggle(idx)}
                          className={
                            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-300 dark:focus:ring-zinc-700 " +
                            (togglingRows.has(idx) ? "opacity-50 cursor-wait " : "") +
                            (v.isActive === false
                              ? "bg-zinc-300 dark:bg-zinc-700"
                              : "bg-emerald-500 dark:bg-emerald-600")
                          }
                        >
                          <span
                            className={
                              "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform " +
                              (v.isActive === false ? "translate-x-1" : "translate-x-[18px]")
                            }
                          />
                        </button>
                        <span className={"text-[11px] font-medium " + (v.isActive === false ? "text-zinc-500 dark:text-zinc-400" : "text-emerald-700 dark:text-emerald-300")}>
                          {v.isActive === false ? "Inactive" : "Active"}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-2.5">
                    <div className="grid min-w-[390px] grid-cols-[minmax(220px,1fr)_auto] items-start gap-1.5">
                      <input
                        value={v.sku || ""}
                        onChange={(e) => onUpdate(idx, { sku: e.target.value })}
                        className={
                          "w-full min-w-[220px] font-mono " +
                          inputBaseClass +
                          " " +
                          (skuBad
                            ? "border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100"
                            : skuConflict
                              ? "border-amber-400 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
                            : "border-zinc-200 bg-white text-zinc-900 dark:border-zinc-800 dark:text-zinc-100")
                        }
                        placeholder="SKU"
                      />

                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => onCopySku(sku)}
                          className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-semibold hover:bg-zinc-50
                                     dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:bg-zinc-900/60"
                        >
                          Copy
                        </button>

                        <button
                          type="button"
                          onClick={() => onAutoFillRowSku(idx)}
                          className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-semibold hover:bg-zinc-50
                                     dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:bg-zinc-900/60"
                        >
                          Auto-fill
                        </button>
                      </div>

                      {sku && (
                        <div className="col-span-2 text-[11px] font-mono text-zinc-500 break-all dark:text-zinc-400">
                          {sku}
                        </div>
                      )}

                      {row.errs.skuMissing && (
                        <div className="col-span-2 text-[11px] text-red-600 dark:text-red-300">SKU is required.</div>
                      )}
                      {row.errs.skuDup && (
                        <div className="col-span-2 text-[11px] text-red-600 dark:text-red-300">Duplicate SKU detected.</div>
                      )}
                      {skuConflict && (
                        <div className="col-span-2 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                          This SKU is already used by another product.
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-2.5">
                    <input
                      value={v.price ?? ""}
                      onChange={(e) => onUpdate(idx, { price: e.target.value })}
                      className={
                        "w-28 " +
                        inputBaseClass +
                        " " +
                        (priceBad
                          ? "border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100"
                          : "border-zinc-200 bg-white text-zinc-900 dark:border-zinc-800 dark:text-zinc-100")
                      }
                      inputMode="decimal"
                      placeholder="0.00"
                    />
                    {priceBad && (
                      <div className="mt-1 text-[11px] text-red-600 dark:text-red-300">Enter a valid price.</div>
                    )}
                  </td>

                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {row.count === 0 && !skuConflict ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800
                                         dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200">
                          Ready
                        </span>
                      ) : (
                        <>
                          {skuConflict && (
                            <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                              IN USE
                            </span>
                          )}
                          {row.errs.skuMissing && (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800
                                             dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                              SKU
                            </span>
                          )}
                          {row.errs.skuDup && (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800
                                             dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                              DUP
                            </span>
                          )}
                          {row.errs.priceInvalid && (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800
                                             dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                              PRICE
                            </span>
                          )}
                          {row.errs.inactiveVariant && (
                            <span className="inline-flex items-center rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700
                                             dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                              INACTIVE
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
